<template>
 <!--
   控除コンポーネント(子)

   **id ではなく class。** 元は id="holidaywork" だった（休日出勤の
   コンポーネントからの写し間違い）。この部品は1か月ぶん繰り返し描かれるので、
   同じ id が31個並び、さらに休日出勤側の31個とも重なっていた。
 -->
 <div class="roster-cell roster-cell--deduction">
  <p class="roster-cell__value">
    <span>{{ deductResult[index] }}</span>
  </p>
 </div>
</template>

<script>
import moment from 'moment';
import ConstList from './../ConstList';

export default {
  name: 'Deduction',
  props: {
    monthDay: Array,
    startBasic: Array,
    endBasic: Array,
    breakBasic: Array,
    start: Array,
    end: Array,
    rest: Array,
    attendance1: Array,
    attendance2: Array,
    // 会社の規定勤務時間（自社設定の work_basic）。日ごとに配られる。
    // 終日欠勤の控除に使う。OverTime.vue も同じものを受け取っている。
    workBasic: Array,
    // 案件の休憩の時間帯（始まり・終わり）。日ごとに配られる。
    // 遅刻・早退の区間に重なる休憩を、控除から除くのに使う。
    breakStartBasic: Array,
    breakEndBasic: Array,
    index: Number
  },
  computed: {
    /*
     * 控除は「値を返す」だけにする。
     *
     * 以前は null を返す computed が data の配列を splice で書き換え、
     * その配列を deep watch が拾って親へ渡していた。computed は読まれた
     * ときにしか動かないので、**いつ親へ届くかが描画の順番まかせ**になる。
     *
     * 2026-08-07、勤怠を常勤からシフトへ直した保存で、日次には直った値
     * （控除なし）が入り、月次合計だけ古い 01:00 が入った。月次集計の
     * 欠勤時間は画面が送ってきた合計をそのまま保存する（サーバは差し替え
     * ない）ので、**勤務表 0:00 / 月次集計 1:00 が出し直しても直らない**
     * 状態になった。
     *
     * **ただし、この直しがあの障害の原因を直したとは言えない。**
     * 試験で再現したのは「部品だけを動かすと親が古い値を持ち続ける」ことで、
     * 本番で起きた形そのものではない。当時の本番JSは現在のソースと同一で、
     * 画面の連鎖を組んでも再現しなかった。原因は未解明のまま。
     * ここは同型の壊れ方を防ぐ防御強化であって、調査を終える根拠ではない。
     *
     * 時間外（OverTime.vue）は先に同じ形へ直してある。そちらに合わせる。
     */
    deductResult () {
      return (this.start || []).map((_, i) => this.deductionAt(i));
    }
  },
  watch: {
    /*
     * 値が変わったら必ず親へ渡す。immediate が無いと、誰かがこの computed を
     * 読むまで親は古い値を持ち続ける。
     *
     * **emit は $nextTick へ回すこと。** 親（Roster.vue）は受け取った値を
     * $set で配列へ書き戻す。描画の最中に同期で emit すると、子を patch して
     * いる途中で親の更新が走り、$scopedSlots を読めずに落ちる。
     * 勤務表は1か月ぶんの子をまとめて描くので、必ず踏む。
     */
    deductResult: {
      immediate: true,
      deep: true,
      handler (values) {
        const value = values[this.index];
        this.$nextTick(() => {
          this.$emit('updateDeduct', {
            index: this.index,
            value
          });
        });
      }
    }
  },
  methods: {
    /*
     * 1日ぶんの控除を出す。
     *
     * **計算そのものは変えていない。** 1年以上の運用実績がある値なので、
     * 直したのは「いつ親へ渡すか」だけ。
     */
    /*
     * その日の規定勤務時間。
     *
     * 未設定・空・"00:00" は 08:00 として扱う。バックエンドも
     * AttendanceBufferEntity で work_basic が null なら 08:00 を返すので、
     * 既定値をここだけ変えると画面とサーバがずれる。
     * Roster.vue は値が無い日に "00:00" を入れるため、そこも拾う。
     */
    workBasicAt (i) {
      const value = (this.workBasic || [])[i];
      if (!value || value === '00:00' || value === '00:00:00') {
        return '08:00';
      }
      return String(value).slice(0, 5);
    },

    deductionAt (i) {
      if (
        this.attendance1[i] === ConstList.HOLIDAY ||
        this.attendance1[i] === ConstList.PAID ||
        this.attendance1[i] === ConstList.SUBSTITUTE ||
        this.attendance1[i] === ConstList.SPECIAL_HOLIDAY
      ) {
        return "";
      }

      /*
       * 終日欠勤は、会社の規定勤務時間ぶんを控除する。
       *
       * **ここは長く '08:00' と直接書いてあった。**「自社の標準時間を控除
       * （弊社８H）」という TODO が残ったまま、自社の8時間だけが入っていた。
       * 所定が8時間でない会社・月では、控除が実態と合わない。
       * 自社設定の「規定勤務時間」（work_basic）は画面から編集でき、
       * 未設定なら 08:00 になる。同じ値を時間外（OverTime.vue）は既に
       * 使っている。控除だけが取り残されていた。
       */
      if (this.attendance1[i] === ConstList.DEDUCTION) {
        return this.workBasicAt(i);
      }

      /*
       * 半日の欠勤は、規定勤務時間の半分を控除する。
       *
       * **2026-09-02 に欠勤の休暇申請を足したとき、午前欠勤(11)と
       * 午後欠勤(16)を勤務表で選べるようにした。だが控除はここを
       * 通らず 0 のままだった。**
       *
       *   午前欠勤＋午後出勤 → 下の「午後出勤は遅刻扱いにしない」に
       *                        当たり、13:00 出社の遅れが消える
       *   午前出勤＋午後欠勤 → 下の「午前出勤は早退扱いにしない」に
       *                        当たり、12:00 退社の早上がりが消える
       *
       * どちらも**休んだ半日が無給にならない。**終日欠勤だけが
       * 規定勤務時間ぶんを控除していて、半日には通り道が無かった。
       *
       * 端数は少ないほうへ寄せる。控除は減額なので、割り切れない
       * ときに従業員が損をしないほうへ倒す。
       */
      /*
       * **足し算にする。**前は「午前欠勤 または 午後欠勤」で、
       * 両方選んだ日でも半日ぶんしか引かれなかった。
       * 1日まるごと休んでいるのに控除が半分。
       *
       * 勤務表では同じ種類の午前＋午後を選べないようにしたが、
       * **すでに保存されている行は残る。**そちらも正しく引く。
       */
      const 半日 = Math.floor(
        moment.duration(this.workBasicAt(i), 'HH:mm').asMinutes() / 2);
      let halfDayAbsence = 0;
      if (this.attendance1[i] === ConstList.MORNING_DEDUCTION) {
        halfDayAbsence += 半日;
      }
      if (this.attendance2[i] === ConstList.AFTERNOON_DEDUCTION) {
        halfDayAbsence += 半日;
      }

      let sTime = moment.duration(this.start[i], 'HH:mm');
      let eTime = moment.duration(this.end[i], 'HH:mm');
      let sBasic;
      let eBasic;

      // 午前出勤・午後有給・午後代休・午後特休・シフトは早退扱いにしない
      // 午後欠勤は上で半日ぶんを引いているので、早退として二重に引かない
      if (this.attendance1[i] === ConstList.MORNING_WORK
        || this.attendance2[i] === ConstList.AFTERNOON_PAID
        || this.attendance2[i] === ConstList.AFTERNOON_SUBSTITUTE
        || this.attendance2[i] === ConstList.AFTERNOON_SPECIAL_HOLIDAY
        || this.attendance2[i] === ConstList.AFTERNOON_DEDUCTION
        || this.attendance2[i] === ConstList.SHIFT
      ) {
        eBasic = moment.duration(this.end[i], 'HH:mm');
      } else {
        eBasic = moment.duration(this.endBasic[i], 'HH:mm');
      }

      // 午後出勤・午前有給・午前代休・午前特休・シフトは遅刻扱いにしない
      // 午前欠勤は上で半日ぶんを引いているので、遅刻として二重に引かない
      if (this.attendance2[i] === ConstList.AFTERNOON_WORK
        || this.attendance1[i] === ConstList.MORNING_PAID
        || this.attendance1[i] === ConstList.MORNING_SUBSTITUTE
        || this.attendance1[i] === ConstList.MORNING_SPECIAL_HOLIDAY
        || this.attendance1[i] === ConstList.MORNING_DEDUCTION
        || this.attendance2[i] === ConstList.SHIFT
      ) {
        sBasic = moment.duration(this.start[i], 'HH:mm');
      } else {
        sBasic = moment.duration(this.startBasic[i], 'HH:mm');
      }

      // 遅刻の区間（定時の始まり〜出勤）と早退の区間（退勤〜定時の終わり）の端。
      // すぐ下の subtract が sTime / eBasic を書き換えるので、その前に分で控える。
      const lateFrom = sBasic.asMinutes();
      const lateTo = sTime.asMinutes();
      const earlyFrom = eTime.asMinutes();
      const earlyTo = eBasic.asMinutes();

      // 時間差分設定（subtract は受け手を書き換えるので sTime / eBasic が差分になる）
      const sDiff = sTime.subtract(sBasic);
      const eDiff = eBasic.subtract(eTime);

      // 00:00は空白
      // **ただし半日欠勤ぶんは残す。**時刻が未入力でも休んだ事実は消えない。
      if ((eTime.hours() === 0 && eTime.minutes() === 0) ||
          (sBasic.hours() === 0 && sBasic.minutes() === 0)) {
        return this.formatMinutes(halfDayAbsence);
      }

      /*
       * 遅刻と早退を足す。**マイナスは 0 にしてから足す。**
       *
       * 前は「片方だけプラスならそちらを返す／両方なら足す」と
       * 三分岐で書いてあった。**結果は同じ**——早く来たぶんで
       * 早退を相殺しない、という同じ約束を短く書いただけ。
       * ここへ半日欠勤ぶんを足す必要があったので一本にまとめた。
       */
      const late = sDiff.asMinutes() > 0 ? sDiff.asMinutes() : 0;
      const early = eDiff.asMinutes() > 0 ? eDiff.asMinutes() : 0;
      /*
       * **案件の休憩の時間帯は、控除しない。**
       *
       * 遅刻・早退は「定時の始まり〜出勤」「退勤〜定時の終わり」の長さで、その中に
       * 案件の休憩（昼休み）が入っていても、そのまま引いていた（2024年から同じ式）。
       * 定時 09:00〜18:00・休憩 12:00〜13:00 の日に 09:00〜10:30 で早退すると、
       * 勤務 1:30・控除 7:30 で合計 9:00。所定の 8:00 より1時間多く引いていた
       * （2026-10-06 の報告。休憩はもともと無給なので、引くものではない）。
       *
       * 休憩の時間帯が、遅刻の区間・早退の区間に重なる分だけを引く。
       * 昼休みのあとで早退した日・昼休みの前に来た遅刻は重ならないので、今までと同じ値。
       * 半休の日も、遅刻・早退の区間が休んだ半日の側へ伸びないので、同じ値。
       * 半日欠勤・終日欠勤の分には触らない。
       *
       * 休憩の時間帯が届いていないとき（案件の設定を読めなかったとき）は、何も引かない
       * （今までと同じ値）。
       */
      const breakInLate = late > 0 ? this.breakOverlap(i, lateFrom, lateTo) : 0;
      const breakInEarly = early > 0 ? this.breakOverlap(i, earlyFrom, earlyTo) : 0;
      /*
       * **除く分は、案件の休憩の長さまで。**休憩の長さが 00:00 の案件（休憩なし）は、
       * 時間帯が入っていても何も除かない。時間帯と長さが食い違う案件で、控除を引きすぎない。
       */
      const breakLength = this.minutesOrNull(this.breakBasic && this.breakBasic[i]);
      const breakLimit = breakLength === null ? 0 : breakLength;
      const breakOut = Math.min(breakInLate + breakInEarly, breakLimit);
      const lateAndEarly = Math.max(0, late + early - breakOut);
      return this.formatMinutes(lateAndEarly + halfDayAbsence);
    },
    /**
     * その日の案件の休憩の時間帯が、区間 from〜to（分）に重なる長さ（分）。
     * 時間帯が無い・読めないときは 0。
     *
     * **同じ日の中に収まる定時・休憩だけを扱う。**次のときは 0（今までと同じ値のまま）。
     * - 休憩の始まりと終わりが同じ時刻（休憩なしの案件。24時間の休憩と読まない）
     * - 休憩が日をまたぐ（終わりが始まりより前）
     * - 定時が日をまたぐ（終わりが始まり以前。22:00〜翌06:00 など）
     *
     * 定時が日をまたぐ案件の遅刻・早退は、区間の作りそのものが日をまたぐ形になっていない
     * （早退が出ない。前からの別の問題）。ここで休憩だけ翌日へずらしても合わないので、
     * 触らない。直すときは、定時・勤務・休憩を同じ時間軸へそろえる所から。
     */
    breakOverlap (i, from, to) {
      const breakFrom = this.minutesOrNull(this.breakStartBasic && this.breakStartBasic[i]);
      const breakTo = this.minutesOrNull(this.breakEndBasic && this.breakEndBasic[i]);
      const projectStart = this.minutesOrNull(this.startBasic && this.startBasic[i]);
      const projectEnd = this.minutesOrNull(this.endBasic && this.endBasic[i]);
      if (breakFrom === null || breakTo === null || breakTo <= breakFrom
          || projectStart === null || projectEnd === null || projectEnd <= projectStart
          || !(to > from)) {
        return 0;
      }
      return Math.max(0, Math.min(to, breakTo) - Math.max(from, breakFrom));
    },
    /** 'HH:mm'（秒つきも）を分にする。空・読めない値は null。 */
    minutesOrNull (value) {
      const match = /^(\d{1,2}):(\d{2})/.exec(String(value == null ? '' : value).trim());
      return match ? Number(match[1]) * 60 + Number(match[2]) : null;
    },
    /**
     * 分を HH:mm にする。0 以下は空白。
     *
     * <p>formatTime は moment の duration を受け取り、24時間で
     * 折り返す。控除は分で足し合わせたいのでこちらを使う。</p>
     */
    formatMinutes (total) {
      if (!total || total <= 0) {
        return "";
      }
      const h = Math.floor(total / 60);
      const m = Math.round(total % 60);
      return (h < 10 ? '0' + h : h) + ':' + (m < 10 ? '0' + m : m);
    },
    formatTime(duration) {
      const hours = duration.hours() < 10 ? '0' + duration.hours() : duration.hours();
      const minutes = duration.minutes() < 10 ? '0' + duration.minutes() : duration.minutes();
      return hours + ':' + minutes;
    }
  }
};
</script>


<!-- Add "scoped" attribute to limit CSS to this component only -->

<style scoped>
h1,
h2 {
  font-weight: normal;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  display: inline-block;
  margin: 0 10px;
}
</style>
